import { BoxProps, Flex } from '@chakra-ui/react';
import type { NestedHome, NestedUser } from '@habity/types';
import { HomeImageCard } from './ImageCard';
import { HomeCardData } from './components/HomeCardData';
import { useAppStore } from '~/store';
import { useUserAction } from '~/hooks/useUserAction';

interface HomeCardProps {
  home: NestedHome;
  user?: NestedUser;
  isLoggedIn?: boolean;
  href: string;
  actions?: boolean;
  minH?: string;
  onClick?: () => void;
  isLoading?: boolean;
  addedToWishlist?: boolean;
  variant?: 'default' | 'compact' | 'display';
  minW?: BoxProps['minW'];
  aspectRatio?: number;
  showBottomBorder?: boolean;
  debug?: boolean;
}

export const HomeCard = ({
  home,
  user,
  isLoggedIn = false,
  href,
  actions = false,
  isLoading = false,
  addedToWishlist = false,
  variant = 'default',
  minW = '260px',
  aspectRatio = 1 / 1.2,
  showBottomBorder = true,
  debug = false,
}: HomeCardProps) => {
  const isMobile = useAppStore((state) => state.isMobile);
  const { executeAction } = useUserAction();

  // Use isLoggedIn (from server) for blur/access control, user (from client) for wishlist features
  const isAuthenticated = isLoggedIn || !!user;
  const showPublicPreview = home?.status?.show_public_preview;
  const homeHref = !isAuthenticated && showPublicPreview ? undefined : href;

  // When user clicks on a home with public preview enabled (e.g., premarket),
  // use executeAction which handles auth and triggers interest registration
  const onClick = !isAuthenticated && showPublicPreview
    ? () => executeAction({
      home,
      action: 'clicked_public_preview_home',
      redirectTo: href,
    })
    : undefined;

  return (
    <Flex
      overflow="hidden"
      flexDirection={variant === 'compact' ? 'row' : 'column'}
      flexGrow={{ base: 0, sm: 1 }}
      justifyContent="flex-start"
      gap="1"
      minW={isMobile ? (variant === 'display' ? '85%' : '100%') : minW}
      cursor={isLoading ? 'default' : 'pointer'}
      className="group"
    >
      <HomeImageCard
        home={home}
        user={user}
        isLoggedIn={isAuthenticated}
        actions={actions}
        href={homeHref}
        onClick={onClick}
        imageVariant="list"
        aspectRatio={aspectRatio}
        imageProps={{ objectFit: 'cover' }}
        borderTopRadius="4px"
        borderBottomRadius="4px"
        boxProps={{ cursor: isLoading ? 'default' : 'pointer' }}
        maxSlides={
          variant === 'display' || (!isAuthenticated && showPublicPreview)
            ? 1
            : 3
        }
        pagination={{ type: 'bullets' }}
        isLoading={isLoading}
        addedToWishlist={addedToWishlist}
        allowActions={variant === 'default'}
        showStatusBadge={variant !== 'display'}
        showBiddingBadge={false}
        showDebugBadge={debug}
        spacing={variant === 'compact' ? 'compact' : 'default'}
      />
      <HomeCardData
        home={home}
        isLoading={isLoading}
        href={homeHref}
        onClick={onClick}
        variant={variant}
        showBottomBorder={showBottomBorder}
      />
    </Flex>
  );
};
